import {
  Alert,
  Badge,
  Button,
  Group,
  type MantineColor,
  Paper,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import {
  ChecksIcon,
  ClockIcon,
  PackageIcon,
  TruckIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import DateTimeCell from '@/common/components/data-table/DateTimeCell.tsx';
import useWorkOrderRelatedItems from '../hooks/useWorkOrderRelatedItems.tsx';
import type { WorkOrderRelatedAssetMovementDto } from '@/generated/api/pacApi.schemas.ts';
import type { ReactNode } from 'react';

type RelatedLiftStatus =
  | 'Scheduled'
  | 'Open'
  | 'Terminated'
  | 'Cancelled'
  | 'Check Schedule';
type RelatedAssetStatus =
  | 'ON ITS WAY'
  | 'CHECK SCHEDULE'
  | 'ONSITE'
  | 'RETURNED';

type WorkOrderRelatedItemsProps = {
  workOrderId: string;
  scheduledAt: string | null;
  timeZone: string | null;
};

function isLater(value: string | null, scheduledAt: string | null) {
  if (!value || !scheduledAt) return false;
  const valueMs = Date.parse(value);
  const scheduledMs = Date.parse(scheduledAt);
  return (
    Number.isFinite(valueMs) &&
    Number.isFinite(scheduledMs) &&
    valueMs > scheduledMs
  );
}

function displayEnum(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function RelatedLiftStatusIndicator({ status }: { status: RelatedLiftStatus }) {
  let color: MantineColor = 'yellow';
  let icon: ReactNode = <WarningCircleIcon size={12} aria-hidden />;
  let label = 'Unknown';

  if (status === 'Scheduled') {
    color = 'blue';
    icon = <ClockIcon size={12} aria-hidden />;
    label = 'Scheduled';
  } else if (status === 'Check Schedule') {
    color = 'orange';
    icon = <WarningCircleIcon size={12} aria-hidden />;
    label = 'Check Schedule';
  } else if (status === 'Open') {
    color = 'green';
    icon = <ChecksIcon size={12} aria-hidden />;
    label = 'On-Site';
  } else if (status === 'Terminated' || status === 'Cancelled') {
    color = 'gray';
    icon = <TruckIcon size={12} aria-hidden />;
    label = status;
  }

  return (
    <Badge
      size="xs"
      variant="light"
      color={color}
      leftSection={icon}
      aria-label={label}
    >
      {label}
    </Badge>
  );
}

function RelatedAssetStatusIndicator({
  status,
}: {
  status: RelatedAssetStatus;
}) {
  let color: MantineColor = 'yellow';
  let icon: ReactNode = <WarningCircleIcon size={12} aria-hidden />;
  let label = 'Unknown';

  if (status === 'ON ITS WAY') {
    color = 'blue';
    icon = <TruckIcon size={12} aria-hidden />;
    label = "On It's Way";
  } else if (status === 'CHECK SCHEDULE') {
    color = 'orange';
    icon = <WarningCircleIcon size={12} aria-hidden />;
    label = 'Check Schedule';
  } else if (status === 'ONSITE') {
    color = 'green';
    icon = <ChecksIcon size={12} aria-hidden />;
    label = 'On-Site';
  } else if (status === 'RETURNED') {
    color = 'gray';
    icon = <PackageIcon size={12} aria-hidden />;
    label = 'Returned';
  }

  return (
    <Badge
      size="xs"
      variant="light"
      color={color}
      leftSection={icon}
      aria-label={label}
    >
      {label}
    </Badge>
  );
}

function MovementDetails({
  label,
  movement,
  timeZone,
}: {
  label: string;
  movement: WorkOrderRelatedAssetMovementDto;
  timeZone: string | null;
}) {
  const hasDateData =
    movement.deliveryEtaStart ||
    movement.deliveryEtaEnd ||
    movement.deliveredAt;
  const inboundDateType = hasDateData
    ? movement.deliveredAt
      ? 'delivery'
      : 'eta'
    : 'delivery';
  const eventDate =
    label === 'Inbound'
      ? (movement.deliveredAt ?? movement.deliveryEtaEnd)
      : movement.shippedOutAt;
  const emptyDate =
    label === 'Inbound'
      ? inboundDateType === 'delivery'
        ? 'Delivery date unavailable'
        : 'ETA unavailable'
      : 'Return date unavailable';

  return (
    <Stack gap={2}>
      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="sm">
        {movement.carrier ?? 'Unknown Carrier'} ·{' '}
        {movement.trackingNumber ?? 'Unknown Tracking'}
      </Text>
      <DateTimeCell
        value={eventDate}
        sourceTimeZone={timeZone}
        timeZoneStyle="inline"
        timeStampStyle={
          label === 'Inbound'
            ? inboundDateType === 'delivery'
              ? 'shortFixed'
              : 'shortEstimate'
            : 'shortFixed'
        }
        emptyLabel={emptyDate}
        prefix={
          label === 'Inbound'
            ? inboundDateType === 'delivery'
              ? 'Delivered At'
              : 'ETA'
            : undefined
        }
      />
    </Stack>
  );
}

export default function WorkOrderRelatedItems({
  workOrderId,
  scheduledAt,
  timeZone,
}: WorkOrderRelatedItemsProps) {
  const query = useWorkOrderRelatedItems(workOrderId);

  if (query.isLoading) {
    return (
      <Stack gap="xs" aria-label="Loading related items">
        <Skeleton height={70} radius="md" />
        <Skeleton height={90} radius="md" />
      </Stack>
    );
  }

  if (query.isError && !query.data) {
    return (
      <Alert color="gray" title="Related items failed to load">
        <Stack gap="xs">
          <Text size="sm">The work order is still available.</Text>
          <Button
            size="xs"
            variant="light"
            onClick={() => void query.refetch()}
          >
            Retry related items
          </Button>
        </Stack>
      </Alert>
    );
  }

  const lifts = query.data?.lifts ?? [];
  const assetVisits = query.data?.assetVisits ?? [];

  if (!lifts.length && !assetVisits.length) {
    return (
      <Text size="xs" c="dimmed">
        No related items
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'preserve-breaks' }}>
        Based on available lift and asset location history.
        {'\n'}
        Records may be incomplete or unrelated to this specific work order.
      </Text>

      {lifts.length > 0 && (
        <Stack gap="xs">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Lifts
          </Text>
          {lifts.map((lift) => {
            const status =
              lift.status === 'Scheduled' &&
              isLater(lift.startDate, scheduledAt)
                ? 'Check Schedule'
                : (lift.status as RelatedLiftStatus);
            return (
              <Paper key={lift.id} withBorder radius="md" p="sm">
                <Stack gap="xs">
                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={700}>
                      {lift.externalId ?? 'External ID unavailable'}
                    </Text>
                    <RelatedLiftStatusIndicator status={status} />
                  </Group>
                  <Stack gap={2}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      Start
                    </Text>
                    <DateTimeCell
                      value={lift.startDate}
                      sourceTimeZone={timeZone}
                      timeZoneStyle="inline"
                    />
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      End
                    </Text>
                    <DateTimeCell
                      value={lift.endDate}
                      sourceTimeZone={timeZone}
                      timeZoneStyle="inline"
                      emptyLabel="No end date"
                    />
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      {assetVisits.length > 0 && (
        <Stack gap="xs">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Asset visits
          </Text>
          {assetVisits.map((visit) => {
            const eta =
              visit.inbound.deliveryEtaStart ?? visit.inbound.deliveryEtaEnd;
            const status = visit.outbound.shippedOutAt
              ? 'RETURNED'
              : visit.inbound.deliveredAt
                ? 'ONSITE'
                : isLater(eta, scheduledAt)
                  ? 'CHECK SCHEDULE'
                  : 'ON ITS WAY';

            const inboundIssue =
              visit.inbound.status === 'ISSUE'
                ? {
                    title: visit.inbound.carrierStatus ?? 'Unknown Issue',
                    message: visit.inbound.latestActivityStatusDescription,
                  }
                : null;

            return (
              <Paper key={visit.visitId} withBorder radius="md" p="sm">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={700}>
                      {displayEnum(visit.assetType)} · {visit.assetId}
                    </Text>
                    <RelatedAssetStatusIndicator status={status} />
                  </Group>
                  {inboundIssue && (
                    <Alert
                      variant="light"
                      color="orange"
                      title={inboundIssue.title}
                    >
                      {inboundIssue.message}
                    </Alert>
                  )}
                  {visit.inbound.status && (
                    <MovementDetails
                      label="Inbound"
                      movement={visit.inbound}
                      timeZone={timeZone}
                    />
                  )}
                  {visit.outbound.status && (
                    <MovementDetails
                      label="Outbound"
                      movement={visit.outbound}
                      timeZone={timeZone}
                    />
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
